import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { getMyCurrentTeamMembership } from './teamService';
import {
  clearAuthLastActivity,
  clearAuthOAuthPending,
  markAuthOAuthPending,
  touchAuthActivity,
} from '../utils/authIdleTimeout';
import { clearMyTeamMembershipCache } from '../utils/myTeamMembershipCache';
import { clearAllSchoolScopedDataCachesForSignOut } from '../utils/schoolDataCacheVersion';
import { clearTeamSettingsSnapshot } from '../utils/teamSettingsSnapshotCache';
import { clearProfileCardCatalogCaches } from '../utils/profileCardCatalogCache';
import { clearDailyRecordNotesCaches } from '../utils/dailyRecordNotesCache';
import { clearDailyRecordNotesRuntimeState } from './dailyRecordNotesService';
import { clearDailyNoteCategoriesRuntimeState } from './dailyNoteCategoriesService';
import { clearAdminLearningContentMemoryCaches } from '../utils/adminLearningContentCache';
import { clearAdminLearningContentSessionDbSync } from '../utils/adminLearningContentSessionSync';
import {
  readCustomUserProfileImage,
  readGoogleAvatarUrlFromUser,
} from '../utils/userProfileImage';
import { clearUserProfileImageMemoryCaches } from '../utils/userProfileImageCache';
import { publicUrlForUserProfileImagePath } from './userProfileImageStorageService';
import { clearProgramCellBgPaletteCaches } from './programCellBgPaletteService';
import { clearAssignmentChallengeCaches } from './assignmentChallengeService';

/** Supabase 재시작·토큰 회전 등으로 로컬 리프레시 토큰이 무효일 때 */
function isInvalidStoredRefreshError(message: string | undefined): boolean {
  if (!message) return false;
  return /Invalid Refresh Token|Refresh Token Not Found|session_not_found/i.test(message);
}

/** 이메일 인증·비밀번호 재설정 리다이렉트용 (CRA `PUBLIC_URL` 포함) */
export function getAuthRedirectBaseUrl(): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${base}/`;
}

export async function getSession(): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    if (isInvalidStoredRefreshError(error.message)) {
      // supabase 로컬 signOut만 호출하면 idle 시각이 남아 OAuth 복귀 직후 바로 로그아웃될 수 있음
      clearAuthLastActivity();
      await supabase.auth.signOut({ scope: 'local' });
      return null;
    }
    return data.session ?? null;
  }
  return data.session ?? null;
}

export async function signInWithEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  touchAuthActivity();
  return data;
}

export async function signInWithGoogle() {
  markAuthOAuthPending();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getAuthRedirectBaseUrl(),
      queryParams: {
        // 계정 선택 화면을 거치게 해 일부 기기에서 캐시된 세션만으로 실패하는 경우를 줄임
        prompt: 'select_account',
      },
    },
  });
  if (error) {
    clearAuthOAuthPending();
    throw error;
  }
  return data;
}

export interface SignUpWithProfileParams {
  email: string;
  password: string;
  fullName: string;
}

export async function signUpWithEmailProfile(params: SignUpWithProfileParams) {
  const email = params.email.trim();
  const fullName = params.fullName.trim();
  const { data, error } = await supabase.auth.signUp({
    email,
    password: params.password,
    options: {
      emailRedirectTo: getAuthRedirectBaseUrl(),
      data: {
        full_name: fullName,
      },
    },
  });
  if (error) throw error;
  return data;
}

/** 비밀번호 재설정 메일 (가입 시 사용한 이메일). Supabase 대시보드에 Redirect URL 등록 필요 */
export async function sendPasswordResetEmail(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: getAuthRedirectBaseUrl(),
  });
  if (error) throw error;
}

export async function updateUserPassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function resendSignupConfirmationEmail(email: string): Promise<void> {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.trim(),
    options: { emailRedirectTo: getAuthRedirectBaseUrl() },
  });
  if (error) throw error;
}

/**
 * 로그아웃(직접·자동 모두): 공용 PC 에 학생 명단·기록이 남지 않도록 기기 캐시까지 모두 비운다.
 */
export async function signOut(options?: { scope?: 'local' | 'global' }) {
  let userId = '';
  try {
    userId = (await supabase.auth.getSession()).data.session?.user?.id ?? '';
  } catch {
    userId = '';
  }
  clearDailyRecordNotesRuntimeState();
  clearDailyNoteCategoriesRuntimeState();
  clearAdminLearningContentSessionDbSync();
  clearAdminLearningContentMemoryCaches();
  clearUserProfileImageMemoryCaches();
  clearMyTeamMembershipCache();
  clearTeamSettingsSnapshot();
  clearProfileCardCatalogCaches();
  clearDailyRecordNotesCaches();
  clearProgramCellBgPaletteCaches();
  clearAssignmentChallengeCaches();
  clearAllSchoolScopedDataCachesForSignOut(userId ? [userId] : []);
  clearAuthLastActivity();
  /** 기본 local: 이 기기만 로그아웃 — 다른 기기 세션·로그인을 유지한다 */
  await supabase.auth.signOut({ scope: options?.scope ?? 'local' });
}

export type DeleteOwnAccountResult =
  | { ok: true; mode?: 'grace' | 'immediate'; purgeAfter?: string }
  | { ok: false; error: string; message?: string };

function mapDeleteAccountInvokeError(raw: string): string {
  const m = raw.trim();
  if (!m) return '회원탈퇴 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.';
  if (/Failed to send a request to the Edge Function/i.test(m)) {
    return (
      '탈퇴 처리 서버(Edge Function)에 연결하지 못했습니다. ' +
      'Supabase 프로젝트에 함수 delete-own-account가 배포되어 있는지 확인해 주세요. ' +
      '프로젝트 루트에서 Supabase CLI로 로그인·링크한 뒤 ' +
      '`npx supabase functions deploy delete-own-account` 를 실행하면 배포할 수 있습니다. ' +
      '로컬 개발 중이면 `supabase start` 로 Edge 런타임이 떠 있는지도 확인해 주세요.'
    );
  }
  if (/Edge Function returned a non-2xx status code/i.test(m)) {
    return (
      '탈퇴 처리 서버가 오류를 반환했습니다. ' +
      'Supabase 대시보드 → Edge Functions → delete-own-account 로그를 확인하거나, 잠시 후 다시 시도해 주세요.'
    );
  }
  return m;
}

/** 탈퇴 후 복구 가능 기간(일) — Edge Function GRACE_DAYS 와 같아야 함 */
export const ACCOUNT_DELETION_GRACE_DAYS = 14;

export type AccountDeletionMode = 'grace' | 'immediate';

/**
 * 본인 계정 탈퇴. Supabase Edge Function `delete-own-account`가 배포되어 있어야 하며,
 * 요청 본문 이메일이 현재 세션 계정 이메일과 일치할 때만 서버에서 처리됩니다.
 * - 'grace'(기본): 14일 복구 유예 후 자동 영구삭제
 * - 'immediate': 즉시 영구삭제
 */
export async function deleteOwnAccountConfirmedEmail(
  email: string,
  mode: AccountDeletionMode = 'grace'
): Promise<void> {
  const trimmed = email.trim().toLowerCase();
  const { data, error } = await supabase.functions.invoke<DeleteOwnAccountResult>('delete-own-account', {
    body: { email: trimmed, mode },
  });

  if (error) {
    throw new Error(mapDeleteAccountInvokeError(error.message || ''));
  }

  const payload = data;
  if (!payload || typeof payload !== 'object') {
    throw new Error('회원탈퇴 응답을 처리하지 못했습니다.');
  }
  if ('ok' in payload && payload.ok === true) {
    return;
  }
  const err = (payload as DeleteOwnAccountResult & { ok: false }).error;
  const msg = (payload as { message?: string }).message;
  if (err === 'EMAIL_MISMATCH') {
    throw new Error('입력한 이메일이 이 계정의 가입 주소와 일치하지 않습니다.');
  }
  if (err === 'NO_ACCOUNT_EMAIL') {
    throw new Error('이 계정에는 이메일이 없어 이 경로로 탈퇴할 수 없습니다. 고객 지원으로 문의해 주세요.');
  }
  if (err === 'NO_SERVICE_ROLE' || err === 'SERVER_MISCONFIGURED') {
    throw new Error('탈퇴 처리 서버 설정이 완료되지 않았습니다. 관리자에게 문의해 주세요.');
  }
  if (err === 'DELETE_FAILED') {
    throw new Error(msg || '계정 삭제에 실패했습니다. 다른 기기에서 로그아웃한 뒤 다시 시도해 주세요.');
  }
  throw new Error(msg || '회원탈퇴에 실패했습니다.');
}

export type PendingAccountDeletion = { requestedAt: string; purgeAfter: string };

/**
 * 현재 로그인 계정의 탈퇴 대기(14일 유예) 기록. 없으면 null.
 * 테이블이 아직 없거나(마이그레이션 미적용) 조회 실패 시에도 null — 정상 앱 이용을 막지 않는다.
 */
export async function getPendingAccountDeletion(userId: string): Promise<PendingAccountDeletion | null> {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('account_deletion_requests')
    .select('requested_at, purge_after')
    .eq('user_id', userId)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as { requested_at: string; purge_after: string };
  return { requestedAt: row.requested_at, purgeAfter: row.purge_after };
}

/** 탈퇴 대기 계정 복구 */
export async function restoreOwnAccount(): Promise<void> {
  const { error } = await supabase.rpc('restore_own_account');
  if (error) throw new Error(error.message || '계정 복구에 실패했습니다.');
}

export async function updateDisplayName(name: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    data: { full_name: name.trim() || undefined },
  });
  if (error) throw error;
}

/**
 * 프로필 아바타용: 이메일 로컬파트(@ 앞)에서 알파벳·숫자만 이어 붙인 뒤 앞 2자를 대문자로.
 * 영문·숫자가 없으면 로컬파트 맨 앞 2글자.
 */
export function getEmailAvatarInitials(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return '?';
  const local = trimmed.includes('@') ? trimmed.split('@')[0]! : trimmed;
  const ascii = local.replace(/[^a-zA-Z0-9]/g, '');
  if (ascii.length >= 2) return ascii.slice(0, 2).toUpperCase();
  if (ascii.length === 1) return `${ascii[0]!}${ascii[0]!}`.toUpperCase();
  const compact = local.replace(/\s/g, '');
  if (compact.length >= 2) return compact.slice(0, 2).toUpperCase();
  if (compact.length === 1) return `${compact[0]!}${compact[0]!}`;
  return trimmed.slice(0, 2).toUpperCase() || '?';
}

/** 이메일·비밀번호로 가입한 계정인지(소셜 전용이면 비밀번호 변경 불가) */
export async function canChangePassword(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.email) return false;
  return session.user.identities?.some((i) => i.provider === 'email') ?? false;
}

/** 이미 로그인된 세션에서 계정 비밀번호가 맞는지 확인 */
export async function verifyCurrentAccountPassword(password: string): Promise<void> {
  const pw = password.trim();
  if (!pw) throw new Error('계정 비밀번호를 입력해 주세요.');
  const allowed = await canChangePassword();
  if (!allowed) {
    throw new Error('소셜 로그인만 사용 중인 계정은 계정 비밀번호로 잠금을 해제할 수 없습니다.');
  }
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const email = user?.email?.trim();
  if (!email) throw new Error('계정 이메일을 확인할 수 없습니다.');
  const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
  if (error) {
    const raw = error.message ?? '';
    throw new Error(
      /invalid login credentials|invalid/i.test(raw)
        ? '계정 비밀번호가 올바르지 않습니다.'
        : raw || '계정 비밀번호 확인에 실패했습니다.'
    );
  }
  touchAuthActivity();
}

/**
 * 현재 비밀번호 확인 후 새 비밀번호로 갱신합니다.
 * (이미 로그인된 세션에서 `signInWithPassword`로 검증한 뒤 `updateUser` 호출)
 */
export async function updatePasswordWithCurrentVerification(
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const { error: signInErr } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  });
  if (signInErr) {
    const raw = signInErr.message ?? '';
    const msg =
      /invalid login credentials|invalid/i.test(raw)
        ? '현재 비밀번호가 올바르지 않습니다.'
        : raw || '현재 비밀번호 확인에 실패했습니다.';
    throw new Error(msg);
  }
  const { error: upErr } = await supabase.auth.updateUser({ password: newPassword });
  if (upErr) throw upErr;
}

export function onAuthStateChange(
  callback: (session: Session | null, event: AuthChangeEvent) => void
) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session, event);
  });
}

export type MemberRole = 'owner' | 'admin' | 'teacher' | 'viewer';

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: '관장',
  admin: '관리자',
  teacher: '팀장',
  viewer: '멤버',
};

export interface UserDisplayInfo {
  userId: string;
  displayName: string;
  email: string;
  role: MemberRole;
  roleLabel: string;
  /** 승인·활성 팀 소속일 때만 팀 이름. 없으면 빈 문자열 */
  teamDisplayName: string;
  /** 팀 소속 여부(네비·계정 메뉴 표시 분기) */
  hasActiveTeam: boolean;
  profileImage: {
    path: string;
    url: string;
    version: number;
  } | null;
  googleAvatarUrl: string | null;
}

export async function getCurrentUserDisplay(
  workspaceId: string,
  schoolName: string
): Promise<UserDisplayInfo | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return null;

  const user = session.user;
  const displayName =
    (user.user_metadata?.full_name as string) ||
    (user.user_metadata?.name as string) ||
    user.email?.split('@')[0] ||
    '사용자';
  const custom = readCustomUserProfileImage(user.user_metadata as Record<string, unknown> | undefined);
  const profileImage = custom
    ? {
        path: custom.path,
        url: publicUrlForUserProfileImagePath(custom.path),
        version: custom.version,
      }
    : null;
  const googleAvatarUrl = readGoogleAvatarUrlFromUser({
    user_metadata: user.user_metadata as Record<string, unknown> | undefined,
    identities: user.identities,
  });
  const base = {
    userId: user.id,
    displayName,
    email: user.email ?? '',
    profileImage,
    googleAvatarUrl,
  };

  try {
    const membershipRow = await getMyCurrentTeamMembership();
    if (
      membershipRow?.team &&
      membershipRow.membership.status === 'approved' &&
      membershipRow.membership.is_active
    ) {
      const activeRole = membershipRow.membership.role as MemberRole;
      return {
        ...base,
        role: activeRole,
        roleLabel: ROLE_LABELS[activeRole] ?? '멤버',
        teamDisplayName: membershipRow.team.name ?? '',
        hasActiveTeam: true,
      };
    }
  } catch {
    // 팀 스키마가 아직 적용되지 않은 환경을 위한 fallback
  }
  void workspaceId;
  void schoolName;
  return {
    ...base,
    role: 'teacher',
    roleLabel: ROLE_LABELS.teacher,
    teamDisplayName: '',
    hasActiveTeam: false,
  };
}
